import * as analytics from 'analytics/Analytics';
import {
  ACTION_CATEGORY,
  ActionOrigin,
  ANALYTICS_MODULE,
  AUDIT_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {TestOrderDataModAction} from 'sample-tests/test-orders/TestOrderDomainTypes';
import {InventoryRegistryDataModAction} from 'samples/getInventoryRegistryChangeSummary';
import {ConfigPanelConfig} from 'tenants/admin/configs/__generated/ConfigPanelConfig';
import {ConfigNames, RationaleConfigValues} from 'tenants/admin/data-modifications/DataModsConfigConstants';
import APIUtil from 'util/APIUtil';
export interface DataModificationsAnalayticsParams {
  module: keyof typeof ANALYTICS_MODULE;
  actionOrigin: ActionOrigin;
  modifiedAction:
    | keyof typeof InventoryRegistryDataModAction
    | keyof typeof TestOrderDataModAction
    | 'STUDY_DESIGN_UPDATE'
    | 'STUDY_PHASE_UPDATE'
    | 'REGISTRY_MERGE'
    | 'REGISTRY_SETTINGS_COLLABORATORS';
  numModifiedTotalItems: number;
  entryId: string | null;
}

interface DataModificationsSettingsAnalyticsParams {
  initialConfigOptions: Array<ConfigPanelConfig>;
  newConfigOptions: Array<ConfigPanelConfig>;
  initialEnabledProjectIds: Array<string>;
  newEnabledProjectIds: Array<string>;
  initialDataModsSetting: RationaleConfigValues;
  newDataModsSetting: RationaleConfigValues;
}

interface BaseMetadata {
  action_origin: ActionOrigin;
  modified_action: string;
  num_modified_total_items: number;
  notebook_entry_id?: string | null;
  file_id?: string | null;
}

function getBaseMetadataFromParams(params: DataModificationsAnalayticsParams): BaseMetadata {
  return {
    action_origin: params.actionOrigin,
    modified_action: params.modifiedAction,
    num_modified_total_items: params.numModifiedTotalItems,
    ...(!params.entryId || APIUtil.isEntry(params.entryId)
      ? {notebook_entry_id: params.entryId}
      : {file_id: params.entryId}),
  };
}

export function trackModalOpen(params: DataModificationsAnalayticsParams): void {
  analytics.trackEvent(
    {
      module: ANALYTICS_MODULE[params.module],
      action: 'opened_modal_data_mods',
      actionCategory: ACTION_CATEGORY.READ,
    },
    getBaseMetadataFromParams(params)
  );
}

export function trackModalClose(params: DataModificationsAnalayticsParams): void {
  analytics.trackEvent(
    {
      module: ANALYTICS_MODULE[params.module],
      action: 'closed_modal_data_mods',
      actionCategory: ACTION_CATEGORY.READ,
    },
    getBaseMetadataFromParams(params)
  );
}

export function trackModalSubmit(params: DataModificationsAnalayticsParams, reasonCode: string): void {
  analytics.trackEvent(
    {
      module: ANALYTICS_MODULE[params.module],
      action: 'submitted_modal_data_mods',
      actionCategory: ACTION_CATEGORY.WRITE,
    },
    {
      ...getBaseMetadataFromParams(params),
      reason_code: reasonCode,
    }
  );
}

export function trackDataModsSettingsChangeAnalytics(params: DataModificationsSettingsAnalyticsParams): void {
  const {
    initialConfigOptions,
    newConfigOptions,
    initialEnabledProjectIds,
    newEnabledProjectIds,
    initialDataModsSetting,
    newDataModsSetting,
  } = params;

  // Track changes in overall Data Mods setting
  if (initialDataModsSetting !== newDataModsSetting) {
    analytics.trackEvent(AUDIT_ANALYTICS_ACTIONS.UPDATED_DATA_MODS_SETTING, {
      data_mods_setting: newDataModsSetting,
    });
  }

  // Track changes in folder movement settings
  const initialMoveFromSettingValue = initialConfigOptions.find(
    (config) => config.key === ConfigNames.AUDITING_ALLOW_MOVE_FROM_DATA_MODIFICATIONS_ENABLED_PROJECT
  )!.value;
  const newMoveFromSettingValue = newConfigOptions.find(
    (config) => config.key === ConfigNames.AUDITING_ALLOW_MOVE_FROM_DATA_MODIFICATIONS_ENABLED_PROJECT
  )!.value;
  if (initialMoveFromSettingValue !== newMoveFromSettingValue) {
    analytics.trackEvent(AUDIT_ANALYTICS_ACTIONS.UPDATED_DATA_MODS_FOLDER_MOVEMENT_SETTING, {
      folder_movement_setting_name: ConfigNames.AUDITING_ALLOW_MOVE_FROM_DATA_MODIFICATIONS_ENABLED_PROJECT,
      folder_movement_setting_value: newMoveFromSettingValue,
    });
  }

  const initialMoveToSettingValue = initialConfigOptions.find(
    (config) => config.key === ConfigNames.AUDITING_ALLOW_MOVE_TO_DATA_MODIFICATIONS_ENABLED_PROJECT
  )!.value;
  const newMoveToSettingValue = newConfigOptions.find(
    (config) => config.key === ConfigNames.AUDITING_ALLOW_MOVE_TO_DATA_MODIFICATIONS_ENABLED_PROJECT
  )!.value;
  if (initialMoveToSettingValue !== newMoveToSettingValue) {
    analytics.trackEvent(AUDIT_ANALYTICS_ACTIONS.UPDATED_DATA_MODS_FOLDER_MOVEMENT_SETTING, {
      folder_movement_setting_name: ConfigNames.AUDITING_ALLOW_MOVE_TO_DATA_MODIFICATIONS_ENABLED_PROJECT,
      folder_movement_setting_value: newMoveToSettingValue,
    });
  }

  if (newDataModsSetting !== RationaleConfigValues.ENABLED_FOR_SPECIFIC_PROJECTS) {
    /**
     * If Data Mods is not enabled for projects, we want to ignore any possible project-enablement changes
     * that the user may have temporarily made
     */
    return;
  }

  // Track changes in enabled projects
  const projectsChanged =
    initialEnabledProjectIds.length !== newEnabledProjectIds.length ||
    !newEnabledProjectIds.every((project) => initialEnabledProjectIds.includes(project));
  if (projectsChanged) {
    analytics.trackEvent(AUDIT_ANALYTICS_ACTIONS.UPDATED_DATA_MODS_ENABLED_PROJECTS, {
      data_mods_enabled_projects: newEnabledProjectIds,
    });
  }
}
